import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Menu, X, Sun, Moon, LogOut } from "lucide-react";
import { useTheme } from "@/hooks/use-theme";
import { useAuth } from "@/hooks/use-auth";
import { useClubMember } from "@/hooks/useClubMember";
import { LogoMark, Wordmark } from "@/components/brand/Logo";
import { NotificationBell } from "@/components/community/NotificationBell";

// One bar, full width, sticky, hairline underneath. Wordmark left, links in
// the middle, theme switch and one orange action on the right. Same shape as
// greeksoup.ai so the two properties read as one family.
// Research Desk and Enterprise live in the footer. GreekSoup opens greeksoup.ai
// until its own page here exists. The Alpha with AI Club left the menu on 30 Oct 2026 (kept for its
// existing members, closed to new sign-ups): it is in the footer, and members get a Club link below.
const navLinks = [
  { label: "Data", href: "/data" },
  // the AI page (31 Oct 2026, his ask): connect ChatGPT, Claude, Gemini and coding tools to SN Data
  { label: "AI Connector", href: "/data/ai" },
  { label: "GreekSoup", href: "https://greeksoup.ai/" },
  { label: "Services", href: "/services" },
  { label: "About", href: "/about" },
];

const isExternal = (href: string) => href.startsWith("http");

export const SN_Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { user, signOut, isLoading: authLoading } = useAuth();
  const { isClubMember, isLoading: clubLoading } = useClubMember(user);
  const isLoading = authLoading || clubLoading;
  // The Club and Newsletter pages keep their member "Login" (it returns to the Club page). Everywhere else a
  // signed-out reader gets Log in and Make a free account (showSignup, below); signed in, everyone gets Account.
  const showLogin = location.pathname.startsWith("/ai-investing-club") || location.pathname === "/newsletter";
  // Enter Club shows on the Club's own pages only (his ruling, 26 Oct 2026: the rest of the site is about data),
  // and only to Club members (30 Oct 2026); elsewhere members get a plain Club link beside Dashboard.
  const inClub = location.pathname.startsWith("/ai-investing-club");
  // the sign-in page shows the Club's own heading when it is reached from a Club page
  const loginTo = inClub ? `/login?redirect=${encodeURIComponent(location.pathname)}` : "/login";
  // Off the Club pages a signed-out reader gets Log in and Make a free account (his go, 30 Oct 2026: sign-in
  // leads with Data now, so the 22 Oct reason for hiding it is gone). The Club pages keep their member sign-in.
  const showSignup = !inClub && location.pathname !== "/newsletter";

  const handleLogout = async () => {
    await signOut();
    setIsOpen(false);
  };

  const linkClass = (href: string) =>
    `whitespace-nowrap text-[14px] font-medium transition-colors ${
      location.pathname === href ? "text-foreground" : "text-muted-foreground hover:text-foreground"
    }`;

  const iconButton =
    "inline-flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-[var(--sn-border-strong)] text-muted-foreground hover:text-foreground transition-colors";

  const cta =
    "inline-flex h-[34px] shrink-0 items-center whitespace-nowrap rounded-lg bg-primary px-3.5 text-[13.5px] font-semibold text-primary-foreground hover:brightness-110 transition";

  return (
    <nav className="fixed inset-x-0 top-0 z-50 border-b border-[var(--sn-border)] bg-background/85 backdrop-blur-xl">
      <div className={`mx-auto px-5 sm:px-8 ${location.pathname.startsWith("/data") ? "max-w-[1320px]" : "max-w-[1120px]"}`}>
        {/* Desktop */}
        <div className="hidden lg:grid h-[60px] grid-cols-[auto_1fr_auto] items-center gap-8">
          <Link to="/" className="flex items-center gap-2" aria-label="Shikshan Nivesh home">
            <LogoMark size={26} />
            <Wordmark size={17} />
          </Link>

          <div className="flex items-center justify-center gap-6">
            {navLinks.map((link) =>
              isExternal(link.href) ? (
                <a key={link.label} href={link.href} className={linkClass(link.href)}>
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className={linkClass(link.href)}
                  aria-current={location.pathname === link.href ? "page" : undefined}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          <div className="flex shrink-0 items-center justify-end gap-2.5 whitespace-nowrap">
            <button
              onClick={toggleTheme}
              className={iconButton}
              aria-label={theme === "dark" ? "Switch to light" : "Switch to dark"}
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            {!isLoading &&
              (user ? (
                <>
                  <Link to="/dashboard" className={linkClass("/dashboard")}>
                    Account
                  </Link>
                  {isClubMember && !inClub && (
                    <Link to="/ai-investing-club/community" className={linkClass("/ai-investing-club/community")}>
                      Club
                    </Link>
                  )}
                  {isClubMember && inClub && (
                    <Link to="/ai-investing-club/community" className={cta}>
                      Enter Club
                    </Link>
                  )}
                  {isClubMember && <NotificationBell userId={user.id} />}
                  <button
                    onClick={handleLogout}
                    className={iconButton}
                    aria-label="Log out"
                    title="Log out"
                  >
                    <LogOut size={15} />
                  </button>
                </>
              ) : showSignup ? (
                <>
                  <Link to="/login" className={linkClass("/login")}>
                    Log in
                  </Link>
                  <Link to="/login?mode=signup" className={cta}>
                    Make a free account
                  </Link>
                </>
              ) : showLogin && (
                <Link to={loginTo} className={cta}>
                  Login
                </Link>
              ))}
          </div>
        </div>

        {/* Mobile and tablet */}
        <div className="lg:hidden flex h-[56px] items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2" aria-label="Shikshan Nivesh home">
            <LogoMark size={24} />
            <Wordmark size={15} />
          </Link>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className={iconButton}
              aria-label={theme === "dark" ? "Switch to light" : "Switch to dark"}
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            {!isLoading &&
              (user ? (
                <Link to="/dashboard" className={`${cta} hidden sm:inline-flex`}>
                  Account
                </Link>
              ) : showSignup ? (
                <Link to="/login?mode=signup" className={`${cta} hidden sm:inline-flex`}>
                  Make a free account
                </Link>
              ) : showLogin && (
                <Link to={loginTo} className={`${cta} hidden sm:inline-flex`}>
                  Login
                </Link>
              ))}
            <button
              className={iconButton}
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              {isOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="lg:hidden border-t border-[var(--sn-border)] py-3">
            <div className="flex flex-col">
              {navLinks.map((link) =>
                isExternal(link.href) ? (
                  <a key={link.label} href={link.href} className={`${linkClass(link.href)} py-2.5`}>
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.label}
                    to={link.href}
                    className={`${linkClass(link.href)} py-2.5`}
                    aria-current={location.pathname === link.href ? "page" : undefined}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                  </Link>
                )
              )}

              {!isLoading &&
                (user ? (
                  <>
                    <Link
                      to="/dashboard"
                      className={`${linkClass("/dashboard")} py-2.5`}
                      onClick={() => setIsOpen(false)}
                    >
                      Account
                    </Link>
                    {isClubMember && (
                      <Link
                        to="/ai-investing-club/community"
                        className={`${linkClass("/ai-investing-club/community")} py-2.5`}
                        onClick={() => setIsOpen(false)}
                      >
                        {inClub ? "Enter Club" : "Club"}
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="inline-flex items-center gap-1.5 py-2.5 text-left text-[14px] font-medium text-muted-foreground hover:text-foreground"
                    >
                      <LogOut size={14} />
                      Logout
                    </button>
                  </>
                ) : showSignup ? (
                  <>
                    <Link to="/login" className={`${linkClass("/login")} py-2.5`} onClick={() => setIsOpen(false)}>
                      Log in
                    </Link>
                    <Link to="/login?mode=signup" className={`${cta} mt-2 justify-center`} onClick={() => setIsOpen(false)}>
                      Make a free account
                    </Link>
                  </>
                ) : showLogin && (
                  <Link to={loginTo} className={`${cta} mt-2 justify-center`} onClick={() => setIsOpen(false)}>
                    Login
                  </Link>
                ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
